import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Eye, Play, Pause } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Campaigns = () => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
  });

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    try {
      const response = await axios.get(`${API}/campaigns`);
      setCampaigns(response.data);
    } catch (error) {
      toast.error('Erro ao carregar campanhas');
    }
    setLoading(false);
  };

  const handleUpdateStatus = async (e, campaignId, currentStatus, newStatus) => {
    e.stopPropagation(); // Prevent card click
    
    try {
      await axios.patch(`${API}/campaigns/${campaignId}/status?new_status=${newStatus}`);
      const statusMessages = {
        running: 'Campanha em execução!',
        paused: 'Campanha pausada',
        completed: 'Campanha concluída'
      };
      toast.success(statusMessages[newStatus] || 'Status atualizado');
      fetchCampaigns();
    } catch (error) {
      toast.error('Erro ao atualizar status');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/campaigns`, formData);
      toast.success('Campanha criada!');
      setDialogOpen(false);
      setFormData({ name: '', description: '' });
      navigate(`/campaigns/${response.data.id}`);
    } catch (error) {
      toast.error('Erro ao criar campanha');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { label: 'Rascunho', className: 'status-draft' },
      scheduled: { label: 'Agendada', className: 'status-scheduled' },
      running: { label: 'Em execução', className: 'status-running' },
      paused: { label: 'Pausada', className: 'status-paused' },
      completed: { label: 'Concluída', className: 'status-completed' },
    };
    const config = statusConfig[status] || statusConfig.draft;
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div data-testid="campaigns-page">
      <div className="mb-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Campanhas</h1>
          <p className="text-gray-600">Crie e gerencie suas campanhas de envio</p>
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="create-campaign-button"
              className="bg-gradient-to-r from-emerald-700 to-green-400 hover:from-emerald-900 hover:to-green-500 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nova Campanha
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Nova Campanha</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Nome da Campanha</Label>
                <Input
                  id="name"
                  data-testid="campaign-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="description">Descrição</Label>
                <Textarea
                  id="description"
                  data-testid="campaign-description-input"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                />
              </div>
              <Button
                data-testid="campaign-submit-button"
                type="submit"
                className="w-full bg-gradient-to-r from-emerald-700 to-green-400 hover:from-emerald-900 hover:to-green-500 text-white"
              >
                Criar Campanha
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {campaigns.length === 0 ? (
        <Card className="border-0 shadow-lg">
          <CardContent className="p-12 text-center">
            <p className="text-gray-500">Nenhuma campanha criada ainda</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {campaigns.map((campaign) => (
            <Card
              key={campaign.id}
              data-testid="campaign-card"
              className="border-0 shadow-lg hover:shadow-xl cursor-pointer"
              onClick={() => navigate(`/campaigns/${campaign.id}`)}
            >
              <CardHeader>
                <div className="flex justify-between items-start">
                  <CardTitle className="text-xl">{campaign.name}</CardTitle>
                  {getStatusBadge(campaign.status)}
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm mb-4">
                  {campaign.description || 'Sem descrição'}
                </p>
                <div className="flex justify-between items-center text-sm text-gray-500 mb-3">
                  <span>{campaign.messages?.length || 0} mensagens</span>
                  <span>{campaign.contact_ids?.length || 0} contatos</span>
                </div>
                
                {/* Action Buttons */}
                <div className="flex gap-2 mb-3">
                  {(campaign.status === 'paused' || campaign.status === 'completed' || campaign.status === 'draft') && (
                    <Button
                      size="sm"
                      onClick={(e) => handleUpdateStatus(e, campaign.id, campaign.status, 'running')}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                    >
                      <Play className="w-3 h-3 mr-1" />
                      Executar
                    </Button>
                  )}
                  
                  {campaign.status === 'running' && (
                    <Button
                      size="sm"
                      onClick={(e) => handleUpdateStatus(e, campaign.id, campaign.status, 'paused')}
                      className="flex-1 bg-yellow-600 hover:bg-yellow-700 text-white"
                    >
                      <Pause className="w-3 h-3 mr-1" />
                      Pausar
                    </Button>
                  )}
                </div>
                
                <Button
                  data-testid="view-campaign-button"
                  variant="outline"
                  className="w-full mt-4"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/campaigns/${campaign.id}`);
                  }}
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Ver detalhes
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default Campaigns;
